import { useEffect, useState } from 'react';

import { useContract, useUpdateContract } from '../../api/hooks';
import { Button, FormField, TextInput, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';

interface DistributionDeductionCardProps {
  contractId: string;
}

const displayRate = (value: number | null | undefined): string => value?.toString() ?? '';

/** Contract-wide deductions that run before residuals, costs, and share payouts. */
export function DistributionDeductionCard({ contractId }: DistributionDeductionCardProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const updateContract = useUpdateContract();
  const [commission, setCommission] = useState('');
  const [expense, setExpense] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const storedCommission = contract.data?.distributionCommissionPct;
  const storedExpense = contract.data?.distributionExpensePct;

  useEffect(() => {
    setCommission(displayRate(storedCommission));
    setExpense(displayRate(storedExpense));
  }, [storedCommission, storedExpense]);

  async function handleSave() {
    const parse = (label: string, value: string): number | null => {
      if (value.trim() === '') return null;
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
        throw new Error(`${label} must be between 0 and 100`);
      }
      return parsed;
    };

    setIsSaving(true);
    try {
      await updateContract.mutateAsync({
        contractId,
        input: {
          distributionCommissionPct: parse('Commission', commission),
          distributionExpensePct: parse('Expenses', expense),
        },
      });
      showToast('Distribution rates saved');
    } catch (error) {
      showToast(errorMessage(error, 'Could not save distribution rates'), 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="rfp-card">
      <div className="rfp-card__head">
        <div>
          <h2 className="rfp-card__title">Distribution Deductions</h2>
          <p className="rfp-card__caption">
            These percentages are deducted once from gross revenue before residuals, costs, and
            participant payouts. Participant rates apply only to that participant’s entitlement.
          </p>
        </div>
      </div>
      <div className="rfp-form-grid">
        <FormField htmlFor="contract-commission" label="Contract Commission %">
          <TextInput
            id="contract-commission"
            inputMode="decimal"
            max="100"
            min="0"
            onChange={(event) => setCommission(event.target.value)}
            placeholder="No contract commission"
            type="number"
            value={commission}
          />
        </FormField>
        <FormField htmlFor="contract-expense" label="Contract Expenses %">
          <TextInput
            id="contract-expense"
            inputMode="decimal"
            max="100"
            min="0"
            onChange={(event) => setExpense(event.target.value)}
            placeholder="No contract expenses"
            type="number"
            value={expense}
          />
        </FormField>
      </div>
      <div className="rfp-wizard-actions">
        <Button
          disabled={isSaving || contract.isLoading}
          onClick={handleSave}
          type="button"
          variant="secondary"
        >
          {isSaving ? 'Saving…' : 'Save distribution rates'}
        </Button>
      </div>
    </section>
  );
}
